import { ButtonHTMLAttributes, FC, ReactNode } from 'react';
import { Container, Icon, Label, Page, Text } from './Button.styles';
import { ArrowBack, ArrowForward } from '@mui/icons-material';

export type ButtonProps =
	| ContainedButton
	| LargeButton
	| IconButton
	| PaginationButton
	| TextButton;

interface ContainedButton extends ButtonHTMLAttributes<HTMLButtonElement> {
	variant: 'contained';
	children: ReactNode;
	fitContent?: boolean;
}

interface LargeButton extends ButtonHTMLAttributes<HTMLButtonElement> {
	variant: 'large' | 'largeRed';
	children: ReactNode;
	fitContent?: boolean;
}

interface IconButton extends ButtonHTMLAttributes<HTMLButtonElement> {
	variant: 'icon';
	icon: JSX.Element;
}

interface PaginationButton extends ButtonHTMLAttributes<HTMLButtonElement> {
	variant: 'pagination';
	active?: boolean;
	page: number | 'prev' | 'next';
}

interface TextButton extends ButtonHTMLAttributes<HTMLButtonElement> {
	variant: 'text';
	children: ReactNode;
}

const Button: FC<ButtonProps> = ({ variant, onClick, ...rest }) => {
	if (variant === 'contained') {
		const { children, fitContent, disabled } = rest as ContainedButton;

		return (
			<Container disabled={disabled} $variant={variant} $fitContent={fitContent} onClick={onClick}>
				<Label>{children}</Label>
			</Container>
		);
	}

	if (variant === 'large' || variant === 'largeRed') {
		const { children, fitContent, disabled } = rest as ContainedButton;

		return (
			<Container disabled={disabled} $variant={variant} $fitContent={fitContent} onClick={onClick}>
				<Label>{children}</Label>
			</Container>
		);
	}

	if (variant === 'icon') {
		const { icon, disabled } = rest as IconButton;

		return (
			<Container disabled={disabled} $variant={variant} onClick={onClick}>
				<Icon>{icon}</Icon>
			</Container>
		);
	}

	if (variant === 'pagination') {
		const { active, page, disabled } = rest as PaginationButton;

		return (
			<Container $active={active} $variant={variant} onClick={onClick} disabled={disabled}>
				<Page>
					{page === 'prev' && <ArrowBack fontSize='inherit' />}
					{page === 'next' && <ArrowForward fontSize='inherit' />}
					{typeof page === 'number' && page}
				</Page>
			</Container>
		);
	}

	if (variant === 'text') {
		const { children, disabled } = rest as TextButton;

		return (
			<Container disabled={disabled} $variant={variant} onClick={onClick}>
				<Text>{children}</Text>
			</Container>
		);
	}

	return null;
};

export default Button;
